import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import {
  getVisibleLineFilterCount,
  isLineChecked,
  isShowingAllLines,
  isVisibleLineFilterActive,
  resolveVisibleLineOrder,
  usePersistedVisibleLines,
  visibleLinesSelectionBase,
} from './usePersistedVisibleLines';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../../services/api';
import { isSnapshotBuilding, snapshotRetryDelayMs } from '../../services/snapshotState';
import { chartSeries, tokens } from './marketingTokens';
import './ai-vis.css';

function RankingLogo({ logoUrl, label }) {
  const [failed, setFailed] = useState(false);
  if (logoUrl && !failed) {
    return (
      <img src={logoUrl} alt="" width={20} height={20} className="aivis-dash-rank-logo"
        onError={() => setFailed(true)} />
    );
  }
  return (
    <span className="aivis-dash-rank-logo aivis-dash-rank-logo--initial">
      {(label || '?')[0].toUpperCase()}
    </span>
  );
}

const SERVICE_LABELS = {
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  google_ai_overviews: 'Google AI Overviews',
};

/**
 * Format Cloro model slugs into readable labels.
 *   gpt-5-5      → GPT 5.5
 *   gpt-5-3-mini → GPT 5.3 mini
 *   gpt-4o       → GPT 4o
 */
function formatModelName(raw) {
  if (!raw) return '';
  const lower = raw.toLowerCase().trim();
  if (lower.startsWith('gpt-')) {
    const parts = lower.slice(4).split('-');
    const numericParts = [];
    const suffixParts  = [];
    let inSuffix = false;
    for (const p of parts) {
      if (!inSuffix && /^\d+$/.test(p)) {
        numericParts.push(p);
      } else {
        inSuffix = true;
        suffixParts.push(p);
      }
    }
    const version = numericParts.join('.');
    const suffix  = suffixParts.join(' ');
    const label   = [version, suffix].filter(Boolean).join(' ');
    return `GPT ${label}`.trim();
  }
  return raw;
}

const LINE_COLORS = chartSeries;

function serviceIconHref(key) {
  if (key === 'chatgpt') return '/chatgpt-icon.png';
  if (key === 'perplexity') return '/perplexity-icon.png';
  if (key === 'google_ai_overviews') return '/google-icon.png';
  return null;
}

function formatShortDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T12:00:00`);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function formatPromptCreated(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const now = new Date();
    const months = Math.floor((now - d) / (1000 * 60 * 60 * 24 * 30));
    if (months >= 1) {
      return `${months} mo. ago`;
    }
    const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (days >= 1) return `${days} day${days === 1 ? '' : 's'} ago`;
    const hr = Math.floor((now - d) / (1000 * 60 * 60));
    if (hr >= 1) return `${hr} hr. ago`;
    return 'Recently';
  } catch {
    return iso;
  }
}

function formatRelativeFinished(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min. ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr. ago`;
    const days = Math.floor(hr / 24);
    if (days < 14) return `${days} day${days === 1 ? '' : 's'} ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

function ChatExecutionModal({ campaignId, executionId, promptText, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!campaignId || !executionId) return undefined;
    let cancelled = false;
    (async () => {
      setLoadError('');
      try {
        const { data } = await api.get(`/aivis/campaigns/${campaignId}/executions/${executionId}/`);
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) setLoadError(e.response?.data?.detail || 'Could not load this chat.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campaignId, executionId]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!executionId) return null;

  const svc = detail?.service;
  const svcLabel = SERVICE_LABELS[svc] || svc || '';

  return (
    <div className="aivis-chat-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="aivis-chat-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aivis-chat-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="aivis-chat-modal-head">
          <div className="aivis-chat-modal-head-left">
            {serviceIconHref(svc) ? (
              <img src={serviceIconHref(svc)} alt="" width={28} height={28} className="aivis-chat-modal-svc-icon" />
            ) : null}
            <span id="aivis-chat-modal-title" className="aivis-chat-modal-title">
              {loadError ? 'Chat' : svcLabel}
            </span>
          </div>
          <div className="aivis-chat-modal-head-actions">
            {promptText ? (
              <span className="aivis-chat-modal-prompt-ref" title={promptText}>
                View prompt: {promptText.length > 80 ? `${promptText.slice(0, 80)}…` : promptText}
              </span>
            ) : null}
            <button type="button" className="aivis-chat-modal-close" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>
        </header>
        <div className="aivis-chat-modal-panes">
          <div className="aivis-chat-modal-main">
            <div className="aivis-chat-modal-user-bubble-row">
              <div className="aivis-chat-modal-user-bubble">{promptText || detail?.prompt_text || '—'}</div>
            </div>
            {loadError ? (
              <p className="aivis-chat-modal-error">{loadError}</p>
            ) : !detail ? (
              <p className="aivis-page-sub">Loading…</p>
            ) : detail.response_text ? (
              <div className="aivis-chat-modal-response aivis-markdown-body">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.response_text}</ReactMarkdown>
              </div>
            ) : (
              <div className="aivis-chat-modal-response">—</div>
            )}
          </div>
          <aside className="aivis-chat-modal-sources" aria-label="Sources">
            <h3 className="aivis-chat-modal-sources-title">Sources</h3>
            {!detail?.execution_sources?.length ? (
              <p className="aivis-prompts-muted">No sources for this response.</p>
            ) : (
              <ul className="aivis-chat-modal-source-list">
                {detail.execution_sources.map((s) => (
                  <li key={s.id} className="aivis-chat-modal-source-item">
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aivis-chat-modal-source-link"
                    >
                      {s.page_title || s.url}
                    </a>
                    <span className="aivis-chat-modal-source-url">{s.url}</span>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

const MAX_POINTS = { D: 30, W: 4, M: 12 };

export default function PromptDetailPage() {
  const { promptId } = useParams();
  const { setSelectedCampaignId } = useOutletContext();

  const [lookup, setLookup] = useState(null);
  const [lookupError, setLookupError] = useState('');
  const [dash, setDash] = useState(null);
  const [chats, setChats] = useState([]);
  const [sectionError, setSectionError] = useState('');
  const [loading, setLoading] = useState(true);
  const [snapshotRetry, setSnapshotRetry] = useState(0);
  const [modalExecutionId, setModalExecutionId] = useState(null);

  const [hoveredLine, setHoveredLine] = useState(null);
  const [chartGranularity, setChartGranularity] = useState('D');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);
  const chartContainerRef = useRef(null);

  // Persisted competitor filter — survives reloads, keyed per campaign + prompt
  const lineOrderForHook = useMemo(() => dash?.series_line_order || [], [dash?.series_line_order]);
  const [visibleLines, setVisibleLines] = usePersistedVisibleLines(lookup?.campaign_id, promptId, lineOrderForHook);

  // Close filter panel when prompt changes
  useEffect(() => { setFilterOpen(false); }, [promptId]);

  // Close filter panel on outside click
  useEffect(() => {
    if (!filterOpen) return;
    function onDown(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [filterOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLookup(null);
      setLookupError('');
      try {
        const { data } = await api.get(`/aivis/prompts/${promptId}/`);
        if (cancelled) return;
        setLookup(data);
        if (data.campaign_id) {
          setSelectedCampaignId(data.campaign_id);
        }
      } catch (e) {
        if (!cancelled) {
          setLookupError(e.response?.data?.detail || 'Prompt not found or you do not have access.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [promptId, setSelectedCampaignId]);

  useEffect(() => {
    if (!lookup?.campaign_id) {
      setLoading(false);
      setDash(null);
      setChats([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setSectionError('');
      try {
        const cid = lookup.campaign_id;
        const [dRes, cRes] = await Promise.all([
          api.get(`/aivis/campaigns/${cid}/prompts/${promptId}/dashboard/`),
          api.get(`/aivis/campaigns/${cid}/prompts/${promptId}/recent-chats/`),
        ]);
        if (cancelled) return;
        setDash(dRes.data);
        const chatPayload = cRes.data;
        const chatList = Array.isArray(chatPayload?.results)
          ? chatPayload.results
          : Array.isArray(chatPayload)
            ? chatPayload
            : [];
        setChats(chatList);
      } catch (e) {
        if (!cancelled) setSectionError(e.response?.data?.detail || 'Could not load prompt analytics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lookup?.campaign_id, promptId, snapshotRetry]);

  const snapshotBuilding = isSnapshotBuilding(dash);
  const snapshotRetryAfter = dash?.retry_after;

  useEffect(() => {
    if (!snapshotBuilding) return undefined;
    const timer = window.setTimeout(
      () => setSnapshotRetry((attempt) => attempt + 1),
      snapshotRetryDelayMs({ retry_after: snapshotRetryAfter }),
    );
    return () => window.clearTimeout(timer);
  }, [snapshotBuilding, snapshotRetryAfter]);

  const lineOrder = useMemo(() => dash?.series_line_order || [], [dash?.series_line_order]);
  const chartData = useMemo(() => {
    return (dash?.series || []).map((row) => ({
      date: row.date,
      label: formatShortDate(row.date),
      ...(row.metrics || {}),
    }));
  }, [dash?.series]);

  // Map label → brand_color from rankings
  const brandColorMap = useMemo(() => {
    const map = {};
    (dash?.rankings || []).forEach((r) => {
      if (r.brand_color) map[r.label] = r.brand_color;
    });
    return map;
  }, [dash?.rankings]);

  function weekStart(dateStr) {
    const d = new Date(`${dateStr}T12:00:00`);
    const day = d.getDay();
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
    return d;
  }

  const aggregatedChartData = useMemo(() => {
    const max = MAX_POINTS[chartGranularity] || 30;
    if (chartGranularity === 'D' || !chartData.length) return chartData.slice(-max);
    const groups = new Map();
    const order = [];
    chartData.forEach((row) => {
      let key, label;
      if (chartGranularity === 'W') {
        const ws = weekStart(row.date);
        key = ws.toISOString().slice(0, 10);
        label = ws.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      } else {
        const d = new Date(`${row.date}T12:00:00`);
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        label = `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]} '${String(d.getFullYear()).slice(2)}`;
      }
      if (!groups.has(key)) { groups.set(key, { label, _s: {}, _n: {} }); order.push(key); }
      const g = groups.get(key);
      lineOrder.forEach((k) => {
        if (typeof row[k] === 'number') {
          g._s[k] = (g._s[k] || 0) + row[k];
          g._n[k] = (g._n[k] || 0) + 1;
        }
      });
    });
    return order.map((key) => {
      const g = groups.get(key);
      const result = { label: g.label };
      lineOrder.forEach((k) => {
        if (g._n[k]) result[k] = Math.round((g._s[k] / g._n[k]) * 10) / 10;
      });
      return result;
    }).slice(-max);
  }, [chartData, chartGranularity, lineOrder]);

  const GRAN_LABELS = { D: 'Daily', W: 'Weekly', M: 'Monthly' };

  const visibleLineOrder = useMemo(
    () => resolveVisibleLineOrder(lineOrder, visibleLines),
    [lineOrder, visibleLines]
  );

  // Y-axis ceiling: data max + 5% buffer, rounded to nearest 5, capped at 100
  const visYMax = useMemo(() => {
    const vals = aggregatedChartData.flatMap((d) => visibleLineOrder.map((k) => (typeof d[k] === 'number' ? d[k] : 0)));
    const peak = vals.length ? Math.max(...vals) : 0;
    return Math.min(100, Math.ceil((peak + 5) / 5) * 5);
  }, [aggregatedChartData, visibleLineOrder]);

  // Compute date range from the sliced aggregated data so the title always matches what's shown
  const dateRange = useMemo(() => {
    if (!aggregatedChartData.length) return null;
    const first = aggregatedChartData[0];
    const last  = aggregatedChartData[aggregatedChartData.length - 1];
    if (chartGranularity === 'D' && first.date) {
      const d1 = new Date(`${first.date}T12:00:00`);
      const d2 = last.date ? new Date(`${last.date}T12:00:00`) : d1;
      const days = Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
      const fmtShort = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const fmtFull  = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      const label = aggregatedChartData.length === 1 ? fmtFull(d1) : `${fmtShort(d1)} – ${fmtFull(d2)}`;
      return { label, days };
    }
    // Weekly / Monthly — derive from labels
    const cnt   = aggregatedChartData.length;
    const label = cnt >= 2 ? `${first.label} – ${last.label}` : first.label;
    return { label, days: cnt };
  }, [aggregatedChartData, chartGranularity]);

  async function downloadChartPng() {
    const card = chartContainerRef.current?.closest('.aivis-dash-card') || chartContainerRef.current;
    if (!card) return;
    card.classList.add('aivis-exporting');
    try {
      const canvas = await html2canvas(card, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
        ignoreElements: (el) => el.classList?.contains('aivis-dash-vis-controls'),
      });
      canvas.toBlob((blob) => {
        const a = document.createElement('a');
        a.download = 'visibility-chart.png';
        a.href = URL.createObjectURL(blob);
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      }, 'image/png');
    } catch (e) {
      console.error('PNG export failed', e);
    } finally {
      card.classList.remove('aivis-exporting');
    }
  }

  const renderLineTooltip = useCallback(
    ({ active, payload, label }) => {
      if (!active || !payload?.length) return null;
      return (
        <div className="aivis-dash-tooltip">
          <div className="aivis-dash-tooltip-title">{label}</div>
          {payload
            .filter((p) => p.dataKey !== 'label' && p.dataKey !== 'date')
            .map((p) => (
              <div key={String(p.dataKey)} className="aivis-dash-tooltip-row">
                <span className="aivis-dash-tooltip-dot" style={{ background: p.color }} />
                <span>{p.name}</span>
                <strong>{typeof p.value === 'number' ? `${p.value}%` : p.value}</strong>
              </div>
            ))}
        </div>
      );
    },
    []
  );

  const promptTitle = dash?.prompt?.text || lookup?.text || '';
  const dateCreatedIso = dash?.prompt?.created_at || lookup?.created_at;

  const closeModal = useCallback(() => setModalExecutionId(null), []);

  if (!lookup && !lookupError) {
    return (
      <div className="aivis-prompt-detail-page">
        <Link to="/ai-vis/prompts" className="aivis-prompt-detail-back">
          ← Back to prompts
        </Link>
        <p className="aivis-page-sub" style={{ padding: '24px 0' }}>
          Loading…
        </p>
      </div>
    );
  }

  if (lookupError) {
    return (
      <div className="aivis-prompt-detail-page">
        <Link to="/ai-vis/prompts" className="aivis-prompt-detail-back">
          ← Back to prompts
        </Link>
        <p className="aivis-auth-error">{lookupError}</p>
      </div>
    );
  }

  return (
    <div className="aivis-prompt-detail-page">
      <nav className="aivis-prompt-detail-breadcrumb" aria-label="Breadcrumb">
        <Link to="/ai-vis/prompts">Prompts</Link>
        <span className="aivis-prompt-detail-bc-sep" aria-hidden>
          /
        </span>
        <span className="aivis-prompt-detail-bc-current">
          {promptTitle ? (promptTitle.length > 64 ? `${promptTitle.slice(0, 64)}…` : promptTitle) : '…'}
        </span>
      </nav>

      <header className="aivis-prompt-detail-hero">
        <p className="aivis-prompt-detail-meta aivis-prompt-detail-meta--top">
          <span className="aivis-prompt-detail-meta-label">Date created</span>
          <span className="aivis-prompt-detail-meta-value">{formatPromptCreated(dateCreatedIso)}</span>
        </p>
        <h1 className="aivis-page-title aivis-prompt-detail-title">{promptTitle || 'Prompt'}</h1>
      </header>

      {sectionError ? <div className="aivis-auth-error">{sectionError}</div> : null}

      <section className="aivis-dash-section" aria-label="Visibility and rankings">
        <div className="aivis-dash-row aivis-dash-row--7-3">
          {/* Visibility chart */}
          <div className="aivis-dash-card">
            <div className="aivis-dash-card-head aivis-dash-vis-head">
              {/* Left: title + date range (only when there's a real range, i.e. 2+ data points) */}
              <h2 className="aivis-dash-card-title">
                Visibility
                {dateRange && aggregatedChartData.length >= 2 && (
                  <span className="aivis-dash-vis-daterange"> · {dateRange.label}</span>
                )}
              </h2>

              {/* Right: meta text (captured in PNG) + controls (excluded from PNG) */}
              <div className="aivis-dash-vis-right">
                {dateRange && (
                  <span className="aivis-dash-vis-meta">
                    {chartGranularity === 'D'
                      ? `${dateRange.days} ${dateRange.days === 1 ? 'day' : 'days'}`
                      : chartGranularity === 'W'
                        ? `${aggregatedChartData.length} ${aggregatedChartData.length === 1 ? 'week' : 'weeks'}`
                        : `${aggregatedChartData.length} ${aggregatedChartData.length === 1 ? 'month' : 'months'}`
                    } · {GRAN_LABELS[chartGranularity]}
                  </span>
                )}
                <div className="aivis-dash-vis-controls">
                  <div className="aivis-dash-gran-toggle">
                    <div
                      className="aivis-dash-gran-slider"
                      style={{ transform: `translateX(${['D','W','M'].indexOf(chartGranularity) * 100}%)` }}
                      aria-hidden
                    />
                    {[['D','Daily'],['W','Weekly'],['M','Monthly']].map(([g, title]) => (
                      <button
                        key={g}
                        type="button"
                        title={title}
                        className={`aivis-dash-gran-btn${chartGranularity === g ? ' aivis-dash-gran-btn--active' : ''}`}
                        onClick={() => setChartGranularity(g)}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                  {/* Competitor filter */}
                  {lineOrder.length > 0 && (
                    <div className="aivis-dash-line-filter-wrap" ref={filterRef}>
                      <button
                        type="button"
                        className={`aivis-dash-chart-menu-btn${isVisibleLineFilterActive(lineOrder, visibleLines) ? ' aivis-dash-chart-menu-btn--active' : ''}`}
                        onClick={() => setFilterOpen((v) => !v)}
                        title="Filter competitors"
                        aria-label="Filter which competitors are shown"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                        </svg>
                        {isVisibleLineFilterActive(lineOrder, visibleLines) && (
                          <span className="aivis-dash-filter-badge">{getVisibleLineFilterCount(lineOrder, visibleLines)}</span>
                        )}
                      </button>
                      {filterOpen && (
                        <div className="aivis-dash-line-filter-panel">
                          <div className="aivis-dash-line-filter-header">
                            <span className="aivis-dash-line-filter-title">Show competitors</span>
                            <button
                              type="button"
                              className="aivis-dash-line-filter-all"
                              onClick={() => {
                                if (isShowingAllLines(lineOrder, visibleLines)) {
                                  setVisibleLines(new Set([lineOrder[0]]));
                                } else {
                                  setVisibleLines(null);
                                }
                              }}
                            >
                              {isShowingAllLines(lineOrder, visibleLines) ? 'Deselect all' : 'Select all'}
                            </button>
                          </div>
                          <ul className="aivis-dash-line-filter-list">
                            {lineOrder.map((key, i) => {
                              const checked = isLineChecked(key, lineOrder, visibleLines);
                              const color = brandColorMap[key] || LINE_COLORS[i % LINE_COLORS.length];
                              return (
                                <li key={key} className="aivis-dash-line-filter-item">
                                  <label className="aivis-dash-line-filter-label">
                                    <input
                                      type="checkbox"
                                      className="aivis-dash-line-filter-check"
                                      checked={checked}
                                      onChange={() => {
                                        const base = visibleLinesSelectionBase(lineOrder, visibleLines);
                                        if (base.has(key)) {
                                          base.delete(key);
                                        } else {
                                          base.add(key);
                                        }
                                        setVisibleLines(base.size === lineOrder.length ? null : base);
                                      }}
                                    />
                                    <span className="aivis-dash-line-filter-dot" style={{ background: color }} />
                                    <span className="aivis-dash-line-filter-name">{key}</span>
                                    {i === 0 && <span className="aivis-dash-line-filter-you">YOU</span>}
                                  </label>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    type="button"
                    className="aivis-dash-chart-menu-btn"
                    onClick={downloadChartPng}
                    title="Download as PNG"
                    aria-label="Download chart as PNG"
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div className="aivis-dash-chart-wrap aivis-dash-chart-wrap--tall" ref={chartContainerRef}>
              {snapshotBuilding ? (
                <p className="aivis-dash-empty">Preparing this prompt’s visibility report from completed runs…</p>
              ) : loading && !aggregatedChartData.length ? (
                <p className="aivis-dash-empty">Loading…</p>
              ) : aggregatedChartData.length && visibleLineOrder.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={aggregatedChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="4 4" stroke={tokens.graphite200} vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke={tokens.graphite500} tickLine={false} />
                    <YAxis domain={[0, visYMax]} tick={{ fontSize: 11 }} stroke={tokens.graphite500} width={40} tickFormatter={(v) => `${v}%`} />
                    <Tooltip content={renderLineTooltip} />
                    <Legend
                      wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
                      onMouseEnter={(data) => setHoveredLine(data.value)}
                      onMouseLeave={() => setHoveredLine(null)}
                    />
                    {visibleLineOrder.map((key, i) => {
                      const isHovered = hoveredLine === key;
                      const isDimmed  = hoveredLine && hoveredLine !== key;
                      const globalIdx = lineOrder.indexOf(key);
                      const stroke    = brandColorMap[key] || LINE_COLORS[globalIdx % LINE_COLORS.length];
                      return (
                        <Line
                          key={key}
                          type="monotone"
                          dataKey={key}
                          name={key}
                          stroke={stroke}
                          strokeWidth={isHovered ? 3 : (key === lineOrder[0] ? 2.2 : 1.8)}
                          strokeOpacity={isDimmed ? 0.15 : 1}
                          dot={{ r: 4, fill: stroke, stroke: '#fff', strokeWidth: 1.5 }}
                          activeDot={{ r: 6, fill: stroke, stroke: '#fff', strokeWidth: 2 }}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <>
                  {aggregatedChartData.length && !visibleLineOrder.length ? (
                    <p className="aivis-dash-empty">No competitors selected. Use the filter to choose which lines to display.</p>
                  ) : (
                    <p className="aivis-dash-empty">No completed data for this prompt yet.</p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Rankings card — matches Dashboard Rankings exactly */}
          <div className="aivis-dash-card">
            <div className="aivis-dash-card-head aivis-dash-card-head--row">
              <div>
                <h2 className="aivis-dash-card-title">Rankings</h2>
                <p className="aivis-dash-card-desc">Your brand vs. tracked competitors by citation share.</p>
              </div>
              <a href="/ai-vis/competitors" className="aivis-dash-rank-expand-btn" title="View all competitors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </a>
            </div>
            <div className="aivis-dash-rankings">
              {snapshotBuilding ? (
                <p className="aivis-dash-empty">Preparing rankings for this prompt…</p>
              ) : (dash?.rankings || []).length ? (
                <ul className="aivis-dash-rank-list">
                  {dash.rankings.slice(0, 6).map((row, idx) => (
                    <li
                      key={`${row.label}-${idx}`}
                      className={`aivis-dash-rank-row${hoveredLine === row.label ? ' aivis-dash-rank-row--hovered' : ''}`}
                      onMouseEnter={() => setHoveredLine(row.label)}
                      onMouseLeave={() => setHoveredLine(null)}
                    >
                      <span className="aivis-dash-rank-num">{idx + 1}</span>
                      <span className="aivis-dash-rank-logo-dot">
                        <RankingLogo logoUrl={row.logo_url} label={row.label} />
                        <span
                          className="aivis-dash-rank-line-dot"
                          style={{ background: brandColorMap[row.label] || LINE_COLORS[idx % LINE_COLORS.length] }}
                          aria-hidden
                        />
                      </span>
                      <span className={`aivis-dash-rank-name ${row.is_client ? 'aivis-dash-rank-name--client' : ''}`}>
                        {row.label}
                        {row.is_client && <span className="aivis-comp-you-tag" style={{ marginLeft: 6 }}>You</span>}
                      </span>
                      <span className="aivis-dash-rank-pct">
                        {row.visibility_pct != null ? `${row.visibility_pct}%` : '—'}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="aivis-dash-empty">Add competitors on the Competitors page to see rankings.</p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="aivis-dash-section" aria-label="Recent responses">
        <div className="aivis-dash-card">
          <div className="aivis-dash-card-head">
            <div>
              <h2 className="aivis-dash-card-title">Recent Responses</h2>
              <p className="aivis-dash-card-desc">
                Up to 100 most recent completed responses (all of them if there are fewer). Scroll the table.
              </p>
            </div>
          </div>
          <div className="aivis-recent-chats-scroll">
            <table className="aivis-recent-chats-table">
              <thead>
                <tr>
                  <th scope="col">Response</th>
                  <th scope="col">Client Mentioned</th>
                  <th scope="col" title="Number of sources in this response">
                    Sources
                  </th>
                  <th scope="col">Created</th>
                </tr>
              </thead>
              <tbody>
                {chats.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="aivis-recent-chats-empty">
                      {loading ? 'Loading…' : 'No responses recorded yet.'}
                    </td>
                  </tr>
                ) : (
                  chats.map((row) => (
                    <tr
                      key={row.id}
                      className="aivis-recent-chats-tr"
                      role="button"
                      tabIndex={0}
                      onClick={() => setModalExecutionId(row.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setModalExecutionId(row.id);
                        }
                      }}
                    >
                      <td className="aivis-recent-chats-chat">
                        <div className="aivis-recent-chats-chat-inner">
                          <div className="aivis-recent-chats-service-wrap">
                            {serviceIconHref(row.service) ? (
                              <img
                                src={serviceIconHref(row.service)}
                                alt=""
                                width={24}
                                height={24}
                                className="aivis-recent-chats-service"
                              />
                            ) : null}
                            {row.model_name ? (
                              <span className="aivis-recent-chats-model" title={row.model_name}>{formatModelName(row.model_name)}</span>
                            ) : null}
                          </div>
                          <span
                            className="aivis-recent-chats-preview"
                            title={row.response_preview || undefined}
                          >
                            {row.response_preview || '—'}
                          </span>
                        </div>
                      </td>
                      <td className="aivis-recent-chats-mentioned">
                        {row.client_mentioned === true ? (
                          <span className="aivis-mentioned aivis-mentioned--yes">Yes</span>
                        ) : row.client_mentioned === false ? (
                          <span className="aivis-mentioned aivis-mentioned--no">No</span>
                        ) : (
                          <span className="aivis-prompts-muted">—</span>
                        )}
                      </td>
                      <td className="aivis-recent-chats-num">{row.sources_count ?? 0}</td>
                      <td className="aivis-recent-chats-time">{formatRelativeFinished(row.finished_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && chats.length > 0 ? (
            <p className="aivis-recent-chats-count" aria-live="polite">
              {chats.length} response{chats.length === 1 ? '' : 's'} loaded
              {chats.length >= 100 ? ' (max 100).' : '.'}
            </p>
          ) : null}
        </div>
      </section>

      <div className="aivis-prompt-detail-footer">
        <Link to="/ai-vis/prompts" className="aivis-prompt-detail-back">
          ← Back to prompts
        </Link>
      </div>

      {modalExecutionId ? (
        <ChatExecutionModal
          campaignId={lookup?.campaign_id}
          executionId={modalExecutionId}
          promptText={promptTitle}
          onClose={closeModal}
        />
      ) : null}
    </div>
  );
}
